/* eslint-disable max-lines */
import { type models } from "jiffy-api";
import { useAtom, useSetAtom } from "jotai";
import { useEffect, useRef, useState } from "react";
import { useFormState, useWatch } from "react-hook-form";

import {
  refineArtworkOpenAtom,
  useArtwork,
  useArtworkForm,
  useClearManualEdits
} from "@design-lab/entities/artwork";
import { useCompositionForm } from "@design-lab/entities/composition";
import { pinningSessionsAtom, useCancelPinningSession } from "@design-lab/entities/pinning";
import {
  trackDesignLabPinningInstructionsRemoved,
  trackExpertEditAddonToggled,
  trackImageEditToolReset,
  trackPrintOptionClicked,
  trackRemoveBackgroundClicked
} from "@design-lab/shared/lib/analytics";
import { getArtworkIdBySide } from "@design-lab/shared/lib/artworkUtils";
import { useShowArtistEditAddon } from "@design-lab/shared/lib/config";
import type { ArtworkForm } from "@design-lab/shared/types";
import { ConfirmActionModal } from "./ConfirmActionModal";
import { QualityTabs } from "./quality-tabs";

type PendingChange =
  | { type: "enhancementTier"; value: models.cart.madeAdd.EnhancementTier }
  | { type: "removeBackground"; value: boolean }
  | { type: "refineArtwork" }
  | null;

type SelectPrintQualityProps = {
  side: "front" | "back";
};

export function SelectPrintQuality({ side }: SelectPrintQualityProps) {
  const { getArtwork } = useArtwork();
  const { form, lens } = useArtworkForm();
  const { form: compositionForm } = useCompositionForm();
  const clearManualEdits = useClearManualEdits();
  const openRefine = useSetAtom(refineArtworkOpenAtom);
  const enableExpertEditAddon = useShowArtistEditAddon();
  const [pinningSessions, setPinningSessions] = useAtom(pinningSessionsAtom);
  const [pendingChange, setPendingChange] = useState<PendingChange>(null);
  const { mutate: cancelSession } = useCancelPinningSession();
  const initializedSidesRef = useRef<Set<string>>(new Set());

  const artworkForm = useWatch({
    control: form.control
  }) as ArtworkForm | undefined;

  const { info } = artworkForm || {};
  const artworkInfoForm = info?.[side];
  const artworkIdForm = artworkForm ? getArtworkIdBySide(artworkForm, side) : "";

  const artwork = artworkIdForm ? getArtwork(artworkIdForm) : null;
  const placementSide = side;

  // Check if there's a session for the current artwork
  const sessionForArtwork = artworkIdForm ? pinningSessions[artworkIdForm] : undefined;
  const hasActiveSession = sessionForArtwork?.state === "active";
  const hasCompletedSession = sessionForArtwork?.state === "completed";
  const hasPinningSession = hasActiveSession || hasCompletedSession;

  // Watch form values
  const jiffyDesignServices = useWatch({
    control: form.control,
    name: `info.${placementSide}.jiffyDesignServices`
  });

  const editImageManual = useWatch({
    control: form.control,
    name: `info.${placementSide}.editImageManual`
  });

  const removeBackground = useWatch({
    control: form.control,
    name: `info.${placementSide}.removeBackground`
  });

  const enhancementRequestNote = useWatch({
    control: form.control,
    name: `info.${placementSide}.enhancementRequestNote`
  });

  // Get form state for validation
  const fieldState = useFormState({
    control: form.control,
    name: `info.${placementSide}.enhancementRequestNote`
  });
  const instructionsError =
    fieldState.errors?.info?.[placementSide]?.enhancementRequestNote?.message;

  // Register the instructions field
  const instructionsFieldName = `info.${placementSide}.enhancementRequestNote` as const;
  const instructionsField = form.register(instructionsFieldName, {
    validate: value => {
      if (jiffyDesignServices && (!value || !value.trim())) {
        return "Comment is required";
      }
      return true;
    }
  });

  // Set jiffyDesignServices to checked if enhancementRequestNote is present on initial load for each artwork
  useEffect(() => {
    // Wait for artwork data to be loaded
    if (!artworkIdForm) {
      return;
    }

    if (initializedSidesRef.current.has(artworkIdForm)) {
      return;
    }

    if (enhancementRequestNote === undefined) {
      return;
    }

    // Now we have confirmed data, mark as initialized
    initializedSidesRef.current.add(artworkIdForm);

    // Auto-check if there's content
    if (enhancementRequestNote && enhancementRequestNote.trim()) {
      form.setValue(`info.${placementSide}.jiffyDesignServices`, true);
    }
  }, [artworkIdForm, enhancementRequestNote, form, placementSide]);

  // Trigger validation when jiffyDesignServices changes
  useEffect(() => {
    if (jiffyDesignServices) {
      form.trigger(instructionsFieldName);
    }
  }, [jiffyDesignServices, form, instructionsFieldName]);

  // Track analytics when jiffyDesignServices is toggled
  useEffect(() => {
    if (jiffyDesignServices === undefined) {
      return;
    }

    const artworkData = form.getValues(placementSide);
    const addedCompositions = compositionForm.getValues("addedCompositions") || [];

    // Extract arrays of composition IDs and catalog IDs
    const compositionIds = addedCompositions.map(comp => comp.id);
    const catalogIds = addedCompositions.map(comp => comp.catalogId);

    trackExpertEditAddonToggled({
      placement: placementSide,
      artworkId: artworkData?.artworkId,
      composition_ids: compositionIds,
      enabled: Boolean(jiffyDesignServices),
      paid_expert_edit_addon: enableExpertEditAddon,
      business_line: "jiffy_made",
      catalog_numbers: catalogIds,
      active_config_section: "full_enhancements",
      source: "design_lab"
    });
  }, [jiffyDesignServices]);

  const executeTabChange = (value: models.cart.madeAdd.EnhancementTier) => {
    const printOptions = {
      print_original: "print_as_is",
      full_enhancements: "print_perfect",
      custom: "artist_edit"
    } as const;

    const printOption = printOptions[value];
    if (!printOption) return;

    trackPrintOptionClicked(printOption);

    form.setValue(`info.${side}.enhancementTier`, value);

    // If switching to "Print As Is", disable remove background
    form.setValue(`info.${side}.removeBackground`, value === "full_enhancements");

    if (value === "print_original") {
      form.setValue(`info.${side}.jiffyDesignServices`, false);
      form.setValue(`info.${side}.enhancementRequestNote`, "");
      // Temporarily disable editImageManual to show original image, but preserve manual edits
      form.setValue(`info.${side}.editImageManual`, false);
    } else if (value === "full_enhancements") {
      // When switching back to PrintPerfect, restore editImageManual if manual edits exist
      const hasManualEdits = artwork?.manualEditUrl || artwork?.manualEditNoTrimUrl;
      if (hasManualEdits) {
        form.setValue(`info.${side}.editImageManual`, true);
      }
    }

    // Don't clear manual edits when switching tabs - preserve them for when user switches back
  };

  const handleTabChange = (value: models.cart.madeAdd.EnhancementTier) => {
    // Check if there's a session that would be invalidated
    if (hasPinningSession) {
      setPendingChange({ type: "enhancementTier", value });
      return;
    }

    executeTabChange(value);
  };

  const executeRemoveBackgroundChange = (checked: boolean) => {
    const artworkId = form.getValues(`${placementSide}.artworkId`);
    if (!artworkId) return;

    if (checked) {
      form.setValue(`info.${placementSide}.enhancementTier`, "full_enhancements");
    }
    clearManualEdits(placementSide);

    trackRemoveBackgroundClicked({
      source: "PrintQuality",
      printQuality: "PrintPerfect",
      side: placementSide,
      artworkId
    });

    form.setValue(`info.${placementSide}.removeBackground`, checked);
  };

  const handleRemoveBackgroundChange = (checked: boolean) => {
    // Check if there's a session that would be invalidated
    if (hasPinningSession) {
      setPendingChange({ type: "removeBackground", value: checked });
      return;
    }

    executeRemoveBackgroundChange(checked);
  };

  const handleJiffyDesignServicesChange = (checked: boolean) => {
    form.setValue(`info.${placementSide}.jiffyDesignServices`, checked);
    if (checked) {
      form.setValue(`info.${placementSide}.enhancementTier`, "full_enhancements");
    } else {
      // Clear the note when unchecking
      form.setValue(`info.${placementSide}.enhancementRequestNote`, "");
    }
  };

  const handleEnhancementRequestNoteChange = (value: string) => {
    form.setValue(`info.${placementSide}.enhancementRequestNote`, value);
  };

  const handleRefineArtworkClick = () => {
    if (hasPinningSession) {
      setPendingChange({ type: "refineArtwork" });
      return;
    }
    form.setValue(`info.${placementSide}.enhancementTier`, "full_enhancements");
    openRefine(true);
  };

  const handleResetManualEdit = () => {
    const artworkId = form.getValues(`${placementSide}.artworkId`);
    if (artworkId) {
      trackImageEditToolReset(artworkId);
    }
    clearManualEdits(placementSide);
  };

  const handleSessionInvalidationConfirm = () => {
    // Clear the pinning session for the current artwork
    if (artworkIdForm && sessionForArtwork) {
      trackDesignLabPinningInstructionsRemoved({
        artworkId: artworkIdForm,
        sessionId: sessionForArtwork.sessionId
      });
      cancelSession(sessionForArtwork.sessionId);
      setPinningSessions(prev => {
        const next = { ...prev };
        delete next[artworkIdForm];
        return next;
      });
    }

    // Execute the pending change
    if (pendingChange?.type === "enhancementTier") {
      executeTabChange(pendingChange.value);
    } else if (pendingChange?.type === "removeBackground") {
      executeRemoveBackgroundChange(pendingChange.value);
    } else if (pendingChange?.type === "refineArtwork") {
      form.setValue(`info.${placementSide}.enhancementTier`, "full_enhancements");
      openRefine(true);
    }

    // Clear the pending change
    setPendingChange(null);
  };

  return (
    <>
      {artworkInfoForm && (
        <QualityTabs
          key={side}
          value={artworkInfoForm.enhancementTier}
          onChange={handleTabChange}
          canRemoveBackground={Boolean(artwork?.canRemoveBg)}
          removeBackground={removeBackground || false}
          jiffyDesignServices={jiffyDesignServices || false}
          enhancementRequestNote={enhancementRequestNote}
          editImageManual={editImageManual || false}
          instructionsError={instructionsError}
          placementSide={placementSide}
          onRemoveBackgroundChange={handleRemoveBackgroundChange}
          onJiffyDesignServicesChange={handleJiffyDesignServicesChange}
          onEnhancementRequestNoteChange={handleEnhancementRequestNoteChange}
          onRefineArtworkClick={handleRefineArtworkClick}
          onResetManualEdit={handleResetManualEdit}
          enhancementRequestNoteField={instructionsField}
          removeBackgroundLens={
            hasPinningSession ? undefined : lens.focus(`info.${placementSide}.removeBackground`)
          }
        />
      )}
      <ConfirmActionModal
        isOpen={pendingChange !== null}
        onClose={() => setPendingChange(null)}
        title="Remove Design Request?"
        message="This change will remove your design request, are you sure you want to proceed?"
        confirmText="Yes, Remove"
        cancelText="Keep Request"
        onConfirm={handleSessionInvalidationConfirm}
      />
    </>
  );
}
